// Primed TNT (RED-8; M15.2.2). PURE (ENG-1): a lightweight physics entity that
// falls like a sand block, counts down a fuse, and detonates via a host callback
// (which routes to EntitySystem.explode → the RED-8 explosion model at power 4).
// Ignited by the host on redstone power, flint & steel, or a neighbouring blast
// (the chain reaction); its physics + timing live here so a unit test can pin
// them without a world or a renderer.
import { TNT } from '../config';
import type { Rng } from '../engine/rng';

export interface PrimedTnt {
  readonly id: number;
  /** Column-centred x,z; `y` is the continuous block-bottom (as a falling block). */
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Game ticks until detonation (counts down; ≤ 0 detonates). */
  fuse: number;
  /** Ticks since ignition (drives the client's white flash). */
  age: number;
}

/** Just enough world for the primed-TNT physics: what stops it. */
export interface TntWorld {
  isSolid(x: number, y: number, z: number): boolean;
}

export class PrimedTntSystem {
  readonly primed: PrimedTnt[] = [];
  private nextId = 1;

  get count(): number { return this.primed.length; }

  /** Prime a TNT at block cell (bx,by,bz) with `fuse` game ticks. It hops upward
   *  (TNT.POP_UP); with an `rng` it also drifts a little horizontally, matching
   *  Java's ignition scatter — pass none for a perfectly vertical (deterministic
   *  golden) pop. */
  spawn(bx: number, by: number, bz: number, fuse: number = TNT.FUSE_GT, rng?: Rng): PrimedTnt {
    const ang = rng ? rng.nextFloat() * Math.PI * 2 : 0;
    const spread = rng ? TNT.POP_SPREAD : 0;
    const t: PrimedTnt = {
      id: this.nextId++,
      x: bx + 0.5, y: by, z: bz + 0.5,
      vx: Math.cos(ang) * spread, vy: TNT.POP_UP, vz: Math.sin(ang) * spread,
      fuse, age: 0,
    };
    this.primed.push(t);
    return t;
  }

  clear(): void { this.primed.length = 0; }

  /** Advance every primed TNT one tick, then detonate those whose fuse ran out via
   *  `detonate` (the host explodes at the entity's centre). `detonate` may prime
   *  more TNT (a chain reaction) — new entities land at the array end and are held
   *  until the next tick, so a blast never recurses within one tick. */
  tick(world: TntWorld, detonate: (t: PrimedTnt) => void): void {
    for (let i = this.primed.length - 1; i >= 0; i--) {
      const t = this.primed[i]!;
      // Gravity + air drag, then a block-aware vertical rest (like a falling block).
      t.vy -= TNT.GRAVITY;
      t.vx *= TNT.DRAG; t.vy *= TNT.DRAG; t.vz *= TNT.DRAG;
      const nx = t.x + t.vx, nz = t.z + t.vz;
      const ny = t.y + t.vy;
      if (t.vy < 0 && world.isSolid(Math.floor(t.x), Math.floor(ny), Math.floor(t.z))) {
        t.y = Math.floor(ny) + 1; t.vy = 0; // rest on top of the obstacle
      } else {
        t.y = ny;
      }
      // Slide horizontally only into open cells (TNT drops off ledges, not through walls).
      if (!world.isSolid(Math.floor(nx), Math.floor(t.y), Math.floor(t.z))) t.x = nx;
      if (!world.isSolid(Math.floor(t.x), Math.floor(t.y), Math.floor(nz))) t.z = nz;
      t.age++;
      if (--t.fuse <= 0) {
        this.primed.splice(i, 1);
        detonate(t);
      }
    }
  }
}
